'use client';

import React from 'react';
import { Template } from './TemplateGallery';

interface Props {
  template: Template;
  isApplied: boolean;
  onApply: () => void;
  onClose: () => void;
}

const previewLinks = [
  { id: 'modal-link-1', label: '🎬 Latest YouTube Video', sub: 'How I built a SaaS in 30 days' },
  { id: 'modal-link-2', label: '📸 Follow on Instagram', sub: '@priya.creates · 340K followers' },
  { id: 'modal-link-3', label: '🛍️ Shop My Merch', sub: 'Limited edition drops' },
  { id: 'modal-link-4', label: '💌 Join Newsletter', sub: '12K subscribers' },
];

export default function TemplatePreviewModal({ template, isApplied, onApply, onClose }: Props) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/40 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-card rounded-2xl card-shadow-lg border border-border max-w-4xl w-full max-h-[90vh] overflow-hidden animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-700 text-foreground">{template.name} Template</h3>
            {!template.free && (
              <span className="text-xs font-700 px-2 py-0.5 rounded-full bg-accent/10 text-accent border border-accent/20">
                Pro
              </span>
            )}
            {template.free && (
              <span className="text-xs font-700 px-2 py-0.5 rounded-full bg-success/10 text-success border border-success/20">
                Free
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            aria-label="Close preview"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal body */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 max-h-[70vh] overflow-auto">
          {/* Preview */}
          <div className={`${template.pageBg} p-8 flex flex-col items-center`}>
            <div className={`w-80 rounded-2xl overflow-hidden border border-border card-shadow-lg`}>
              <div className={`${template.headerBg} p-6 flex flex-col items-center`}>
                <div className="w-16 h-16 rounded-full bg-white/30 border-2 border-white/50 flex items-center justify-center font-800 text-xl text-white mb-3">
                  PS
                </div>
                <p className={`font-700 text-lg ${template.textOnHeader}`}>Priya Sharma</p>
                <p className={`text-xs mt-1 ${template.textOnHeader} opacity-70`}>Content Creator · Mumbai</p>
                <div className="flex gap-2 mt-3">
                  {['📸', '▶', '🐦'].map((icon, i) => (
                    <span
                      key={`modal-social-${i}`}
                      className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm"
                    >
                      {icon}
                    </span>
                  ))}
                </div>
              </div>

              <div className={`p-4 space-y-2.5 ${template.pageBg}`}>
                {previewLinks.map((link) => (
                  <div
                    key={link.id}
                    className={`py-3 px-4 rounded-xl text-sm font-600 ${template.btnStyle} cursor-pointer`}
                  >
                    <p className={template.btnTextClass}>{link.label}</p>
                    <p className="text-xs opacity-60 mt-0.5">{link.sub}</p>
                  </div>
                ))}
                <p className="text-center text-xs text-muted-foreground pt-1">
                  Made with BioLink
                </p>
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="p-8 bg-card flex flex-col justify-between">
            <div>
              <h4 className="text-xl font-700 text-foreground mb-3">{template.name}</h4>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">{template.description}</p>

              <div className="space-y-3 mb-6">
                <p className="text-xs font-600 text-muted-foreground uppercase tracking-wider">Features</p>
                {[
                  'Custom gradient header',
                  'Social icon row',
                  'Styled link buttons',
                  'Mobile optimized',
                  template.free ? 'Available on Free plan' : 'Pro plan required',
                ].map((feat, i) => (
                  <div key={`modal-feat-${i}`} className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-success flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm text-foreground">{feat}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-1.5 mb-6">
                {template.tags.map((tag) => (
                  <span key={`modal-tag-${tag}`} className="text-xs px-3 py-1 rounded-full bg-muted text-muted-foreground font-500">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              {!template.free && (
                <div className="bg-warning/5 border border-warning/20 rounded-xl p-3">
                  <p className="text-xs text-warning font-600">🔒 Pro template</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Upgrade to Pro (₹399/mo) to use this template</p>
                </div>
              )}
              <button
                onClick={() => { onApply(); onClose(); }}
                disabled={isApplied || !template.free}
                className={`w-full py-3 rounded-xl text-sm font-700 transition-all duration-200 ${
                  isApplied
                    ? 'bg-success/10 text-success border border-success/20 cursor-default'
                    : !template.free
                    ? 'bg-muted text-muted-foreground border border-border cursor-not-allowed'
                    : 'btn-primary'
                }`}
              >
                {isApplied ? '✓ Currently Active' : !template.free ? '🔒 Upgrade to Apply' : `Apply ${template.name} Template`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}